<template>
  <div class="self-order-container">
    <div class="self-order-inner">
      <ion-row class="ion-justify-content-center">
        <!-- QR and Code Card Column -->
        <ion-col size-lg="6" size-md="6" size-xs="12" class="col-left">
          <!-- Consolidated Alert Badge -->
          <div class="consolidated-alert-bar" v-if="orderCode && orderCode.length >= 4">
            <div class="alert-icon-box">
              <ion-icon :icon="timeOutline" class="alert-icon"></ion-icon>
            </div>
            <div class="alert-text-box">
              <p class="alert-main-text">
                Tunjukkan QR Code ini ke kasir sebelum jam <strong>{{ orderCode[2] }}:<span v-if="orderCode[3] < 10">0</span>{{ orderCode[3] }}</strong>
              </p>
              <p class="alert-sub-text">
                Pesanan belum akan diproses sebelum Anda menyelesaikan pembayaran di kasir.
              </p>
            </div>
          </div>

          <!-- Ticket Card -->
          <ion-card class="ticket-card" v-if="orderCode">
            <div class="qr-container">
              <div class="qr-frame">
                <ion-img class="qr-image" :src="orderCode[1]" alt="QR Code"></ion-img>
              </div>
            </div>
            
            <div class="ticket-divider">
              <div class="divider-circle left"></div>
              <div class="divider-line"></div>
              <div class="divider-circle right"></div>
            </div>

            <div class="code-container">
              <span class="code-label">KODE PESANAN (ORDER CODE)</span>
              <div class="code-value-wrapper">
                <h2 class="code-value">{{ orderCode[0] }}</h2>
                <ion-button fill="clear" size="small" class="copy-btn" @click="copyCode">
                  <ion-icon :icon="copyOutline" slot="icon-only"></ion-icon>
                </ion-button>
              </div>

              <!-- Subtle Download Link -->
              <div class="download-link-wrapper">
                <span class="download-link" @click="downloadTicket">
                  <ion-icon :icon="downloadOutline" class="dl-icon"></ion-icon>
                  Unduh QR
                </span>
              </div>
            </div>
          </ion-card>
        </ion-col>

        <!-- Personal Info Column -->
        <ion-col size-lg="6" size-md="6" size-xs="12" class="col-right">
          <ion-card class="info-card">
            <div class="info-header">
              <h3>Informasi Pelanggan</h3>
              <p>Lengkapi nama Anda untuk memudahkan kasir memanggil pesanan Anda.</p>
            </div>

            <div class="form-group">
              <label for="customer-name" class="input-label">Nama Lengkap (Opsional)</label>
              <div class="input-container">
                <ion-input 
                  id="customer-name"
                  type="text" 
                  v-model="customer" 
                  class="name-input-field"
                  placeholder="Masukkan nama Anda..."
                  :disabled="disabled"
                ></ion-input>
              </div>
            </div>

            <div class="form-actions">
              <ion-button @click="saveName()" expand="block" class="action-save-btn" :disabled="disabled">
                <ion-icon :icon="saveOutline" slot="start" class="btn-icon"></ion-icon>
                {{ disabled ? 'Nama Tersimpan' : 'Simpan Nama' }}
              </ion-button>
            </div>
          </ion-card>
        </ion-col>
      </ion-row>
    </div>
  </div>
</template>

<script>
import { IonButton, IonCard, IonCol, IonIcon, IonImg, IonInput, IonRow, toastController } from '@ionic/vue';
import { alertCircleOutline, timeOutline, copyOutline, saveOutline, checkmarkCircleOutline, downloadOutline } from 'ionicons/icons';
import { identifyLogRocket } from "@/utils/logrocket";

export default {
  name: "outletOrder",
  props: ["orderCode", "qrCode", "hours", "minutes"],
  components: { IonRow, IonCol, IonCard, IonImg, IonIcon, IonInput, IonButton },
  setup() {
    return {
      alertCircleOutline,
      timeOutline,
      copyOutline,
      saveOutline,
      checkmarkCircleOutline,
      downloadOutline
    };
  },
  data() {
    return {
      customer: "",
      disabled: false,
    };
  },
  beforeMount() {
    let cookieName = this.getCookie("customer");
    if (cookieName != "") {
      this.customer = cookieName;
    }
    if (this.customer != "") {
      this.saveName();
    }
    if (this.customer != "") {
      this.disabled = true;
    }
  },
  methods: {
    async openToast() {
      const toast = await toastController.create({
        message: "Nama anda telah disimpan",
        duration: 3000,
        position: 'bottom',
        color: 'success'
      });
      return toast.present();
    },
    async openToastCopy() {
      const toast = await toastController.create({
        message: "Kode pesanan berhasil disalin",
        duration: 3000,
        position: 'bottom',
        color: 'dark'
      });
      return toast.present();
    },
    async openToastDownloadSuccess() {
      const toast = await toastController.create({
        message: "Tiket berhasil diunduh ke galeri Anda",
        duration: 3000,
        position: 'bottom',
        color: 'success'
      });
      return toast.present();
    },
    copyCode() {
      if (this.orderCode && this.orderCode[0]) {
        navigator.clipboard.writeText(this.orderCode[0]);
        this.openToastCopy();
      }
    },
    async downloadTicket() {
      // 1. Create offscreen canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      const width = 600;
      const height = 800;
      canvas.width = width;
      canvas.height = height;

      // 2. Draw backgrounds & borders
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);

      // Stroked outer border
      ctx.strokeStyle = "#edf2f7";
      ctx.lineWidth = 8;
      ctx.strokeRect(0, 0, width, height);

      // Red header strip
      const brandColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || "#8C1115";
      ctx.fillStyle = brandColor;
      ctx.fillRect(4, 4, width - 8, 16);

      // 3. Draw titles
      ctx.fillStyle = "#1e293b";
      ctx.font = "bold 26px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("UNIQ SELF ORDER TICKET", width / 2, 70);

      ctx.fillStyle = "#64748b";
      ctx.font = "16px sans-serif";
      ctx.fillText("Tunjukkan tiket ini ke kasir untuk proses pembayaran", width / 2, 100);

      // 4. Draw Expiry Notice
      if (this.orderCode && this.orderCode.length >= 4) {
        ctx.fillStyle = "#b45309";
        ctx.font = "bold 16px sans-serif";
        const hour = this.orderCode[2];
        const min = this.orderCode[3] < 10 ? `0${this.orderCode[3]}` : this.orderCode[3];
        ctx.fillText(`Sebelum Jam: ${hour}:${min}`, width / 2, 135);
      }

      // 5. Draw QR code
      const qrImg = new Image();
      qrImg.crossOrigin = "anonymous";
      qrImg.src = this.orderCode[1];

      await new Promise((resolve) => {
        qrImg.onload = resolve;
        qrImg.onerror = resolve;
      });

      // Draw QR border frame
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(width / 2 - 110, 170, 220, 220);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 2;
      ctx.strokeRect(width / 2 - 110, 170, 220, 220);

      // Draw QR image
      ctx.drawImage(qrImg, width / 2 - 95, 185, 190, 190);

      // 6. Draw perforated line
      ctx.beginPath();
      ctx.setLineDash([8, 8]);
      ctx.moveTo(40, 435);
      ctx.lineTo(width - 40, 435);
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.setLineDash([]); // reset

      // 7. Draw Order Code
      ctx.fillStyle = "#94a3b8";
      ctx.font = "bold 16px sans-serif";
      ctx.fillText("KODE PESANAN (ORDER CODE)", width / 2, 490);

      ctx.fillStyle = "#1e293b";
      ctx.font = "bold 60px sans-serif";
      ctx.fillText(this.orderCode[0], width / 2, 560);

      // 8. Draw customer info
      if (this.customer) {
        ctx.fillStyle = "#475569";
        ctx.font = "bold 20px sans-serif";
        ctx.fillText(`Pelanggan: ${this.customer}`, width / 2, 630);
      }

      // 9. Draw warnings
      ctx.fillStyle = "#b91c1c";
      ctx.font = "bold italic 14px sans-serif";
      ctx.fillText("*Pesanan belum akan diproses sebelum Anda menunjukkan QR Code ini di kasir.", width / 2, 690);

      ctx.fillStyle = "#64748b";
      ctx.font = "14px sans-serif";
      ctx.fillText("Powered by UNIQ", width / 2, 740);

      // 10. Download trigger
      try {
        const dataUrl = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.download = `UNIQ-SelfOrder-${this.orderCode[0]}.png`;
        link.href = dataUrl;
        link.click();
        this.openToastDownloadSuccess();
      } catch (err) {
        console.warn("Canvas tainted, falling back to direct QR image download:", err);
        // Fallback: Download the QR image directly
        const link = document.createElement("a");
        link.download = `UNIQ-QR-${this.orderCode[0]}.png`;
        link.href = this.orderCode[1];
        link.target = "_blank";
        link.click();
        
        // Custom warning toast for fallback
        const toast = await toastController.create({
          message: "Tiket diunduh (hanya QR code saja karena pembatasan keamanan)",
          duration: 4000,
          position: 'bottom',
          color: 'warning'
        });
        await toast.present();
      }
    },
    async saveName() {
      if (!this.customer || this.customer.trim() === "") return;
      document.cookie = `customer=${this.customer}`;
      identifyLogRocket({ name: this.customer });
      let myHeaders = new Headers();
      myHeaders.append("Public-Key", `${process.env.VUE_APP_PUBLIC_KEY}`);

      let bodyUrlEncoded = new URLSearchParams();
      bodyUrlEncoded.append("customer_name", this.customer);

      let requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: bodyUrlEncoded,
        redirect: "follow",
      };

      try {
        const req = await fetch(
          `${process.env.VUE_APP_BASE_URL}v1/self-order/${this.orderCode[0]}`,
          requestOptions
        );
        const res = await req.json();
        if (res.status == true) {
          this.openToast();
          this.disabled = true;
        }
      } catch (error) {
        console.error("Error saving name:", error);
      }
    },
    getCookie(cname) {
      let name = cname + "=";
      let decodedCookie = decodeURIComponent(document.cookie);
      let ca = decodedCookie.split(";");
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == " ") {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          return c.substring(name.length, c.length);
        }
      }
      return "";
    },
  },
};
</script>

<style scoped>
/* Container layout */
.self-order-container {
  padding: 10px 8px;
  background: #f8fafc;
  min-height: 100%;
}

.self-order-inner {
  max-width: 800px;
  margin: 0 auto;
}

.col-left, .col-right {
  padding: 8px;
}

/* Consolidated Alert Bar */
.consolidated-alert-bar {
  display: flex;
  gap: 12px;
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  border: 1px solid #fde68a;
  border-radius: 16px;
  padding: 14px 16px;
  margin-bottom: 14px;
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.04);
}

.alert-icon-box {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.alert-icon {
  font-size: 1.4rem;
  color: #d97706;
}

.alert-text-box {
  text-align: left;
}

.alert-main-text {
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  color: #78350f;
  font-weight: 600;
  line-height: 1.4;
}

.alert-sub-text {
  margin: 0;
  font-size: 0.8rem;
  color: #92400e;
  line-height: 1.4;
}

/* Ticket card design */
.ticket-card {
  margin: 0 0 14px 0;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  overflow: visible;
  position: relative;
}

.qr-container {
  padding: 20px 16px 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.qr-frame {
  padding: 12px;
  border-radius: 16px;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 170px;
  height: 170px;
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.01);
}

.qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Perforation Divider */
.ticket-divider {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  height: 16px;
  margin: 6px 0;
}

.divider-circle {
  width: 16px;
  height: 16px;
  background: #f8fafc; /* Match page bg */
  border-radius: 50%;
  position: absolute;
  z-index: 2;
}

.divider-circle.left {
  left: -8px;
  box-shadow: inset -3px 0 4px -1px rgba(0,0,0,0.04);
}

.divider-circle.right {
  right: -8px;
  box-shadow: inset 3px 0 4px -1px rgba(0,0,0,0.04);
}

.divider-line {
  width: 100%;
  border-top: 2px dashed #e2e8f0;
  height: 1px;
}

/* Order Code area */
.code-container {
  padding: 14px 16px 20px 16px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.code-label {
  font-size: 0.7rem;
  color: #94a3b8;
  letter-spacing: 1.2px;
  font-weight: 700;
}

.code-value-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.code-value {
  font-size: 2rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  letter-spacing: 0.5px;
}

.copy-btn {
  --color: var(--primary-color, #8C1115);
  margin: 0;
  --padding-start: 4px;
  --padding-end: 4px;
  height: 28px;
}

/* Subtle Download Link */
.download-link-wrapper {
  margin-top: 12px;
}

.download-link {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.download-link:hover {
  color: var(--primary-color, #8C1115);
  background: #f1f5f9;
}

.dl-icon {
  font-size: 0.85rem;
}

/* Info card form */
.info-card {
  margin: 0;
  padding: 24px 20px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.03);
  border: 1px solid #f1f5f9;
  text-align: left;
}

.info-header h3 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.info-header p {
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
  margin: 0 0 18px 0;
}

.form-group {
  margin-bottom: 18px;
}

.input-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.input-container {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.input-container:focus-within {
  border-color: var(--primary-color, #8C1115);
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
}

.name-input-field {
  --padding-start: 12px;
  --padding-end: 12px;
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 500;
  height: 42px;
}

.action-save-btn {
  --background: var(--primary-color, #8C1115);
  --color: #ffffff;
  --border-radius: 12px;
  height: 46px;
  font-weight: 700;
  font-size: 0.95rem;
  margin: 0;
  --box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  text-transform: none;
}

.action-save-btn[disabled] {
  --background: #cbd5e1;
  --box-shadow: none;
  opacity: 0.7;
}

.btn-icon {
  margin-right: 6px;
  font-size: 1rem;
}
</style>
